/**
 * Bruecke fuer die Gesten des Apple Pencil Pro (Druecken) und des Pencil 2
 * (Doppeltippen).
 *
 * Wichtig: Diese Gesten gibt es im Browser nicht. iPadOS liefert sie nur
 * nativ ueber UIPencilInteraction. Darum:
 *   - In der Web-Version passiert hier nichts; das Werkzeugmenue wird ueber
 *     den kleinen Knopf unten links geoeffnet.
 *   - In der spaeteren App meldet das Capacitor-Plugin die Geste als
 *     Fensterereignis "pencilinteraction". Der Code der App bleibt gleich.
 *
 * Das Plugin-Geruest liegt unter capacitor/pencil-interaction/.
 */

export type PencilGestureType = 'squeeze' | 'double-tap';

export interface PencilGesture {
  type: PencilGestureType;
  /** Bildschirmposition der Stiftspitze, wenn das System sie kennt. */
  x?: number;
  y?: number;
}

const EVENT_NAME = 'pencilinteraction';

/** Meldet sich fuer Pencil-Gesten an. Liefert die Abmeldefunktion. */
export function onPencilGesture(listener: (gesture: PencilGesture) => void): () => void {
  const handler = (ev: Event) => {
    const detail = (ev as CustomEvent<PencilGesture>).detail;
    if (!detail || (detail.type !== 'squeeze' && detail.type !== 'double-tap')) return;
    listener(detail);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/**
 * Loest eine Geste aus. Das Capacitor-Plugin ruft das ueber
 * window.dispatchEvent auf; hier steht es fuer Tests und als klare Schnittstelle.
 */
export function emitPencilGesture(gesture: PencilGesture): void {
  window.dispatchEvent(new CustomEvent<PencilGesture>(EVENT_NAME, { detail: gesture }));
}

/**
 * Verbindet ein vorhandenes Capacitor-Plugin, falls die App nativ laeuft.
 * Im Browser tut die Funktion nichts.
 */
export function connectNativePencil(): void {
  const cap = (window as unknown as {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      Plugins?: Record<string, { addListener?: (name: string, cb: (data: PencilGesture) => void) => void }>;
    };
  }).Capacitor;
  const plugin = cap?.Plugins?.PencilInteraction;
  if (!cap?.isNativePlatform?.() || !plugin?.addListener) return;
  plugin.addListener('pencilInteraction', (data) => emitPencilGesture(data));
}
